import { defineStore } from 'pinia'
import { apiClient, refreshSession } from '@/datasources'
import { clearAuthHint, markAuthHint } from '@/lib/authCookie'
import type { ApiClientResponse } from '@/datasources/apiClient.ts'

// localStorage keys for auth persistence
const AUTH_TOKEN_KEY = 'auth_token'
const AUTH_REFRESH_TOKEN_KEY = 'auth_refresh_token'
const AUTH_USER_KEY = 'auth_user'

// sessionStorage key for the desktop-app handoff marker (scoped to the browser session)
const HANDOFF_SOURCE_KEY = 'handoffSource'

// sessionStorage key for the post-login destination — survives a page reload
// on the login/register screens so a deep link (e.g. /subscribe) is not lost
const REDIRECT_AFTER_LOGIN_KEY = 'redirectAfterLogin'

// one-shot marker: an involuntary logout (dead lease) must tell the login
// page WHY the user landed there instead of dumping them silently
const SESSION_EXPIRED_KEY = 'auth_session_expired'

export type HandoffApp = 'dockit' | 'sqlkit'

type AccountStore = {
  account: {
    id: string
    username: string
    email: string
    password: string
    accessToken: string
    refreshToken: string
    fullName?: string
    avatarUrl?: string
    language?: string
    theme?: string
    emailNotifications?: boolean
    marketingEmails?: boolean
  }
  handoffSource: HandoffApp | null
  showHandoffConfirm: boolean
  redirectAfterLogin: string
}

export enum VerificationTypes {
  PASSWORD = 'PASSWORD',
  CODE = 'CODE',
}

export enum EmailTypes {
  REGISTER = 'REGISTER',
  LOGIN = 'LOGIN',
}

export type AuthRegisterParams = {
  mode: 'password' | 'google'
  email?: string
  password?: string
  full_name?: string
  consent: boolean
  product?: string
  id_token?: string
  code?: number
  turnstile_token?: string
}

// Hydrate auth state from localStorage
const getStoredAuth = () => {
  try {
    const token = localStorage.getItem(AUTH_TOKEN_KEY)
    const userJson = localStorage.getItem(AUTH_USER_KEY)
    if (token && userJson) {
      return {
        ...JSON.parse(userJson),
        accessToken: token,
      }
    }
  } catch (err) {
    console.warn('Failed to parse stored auth, clearing localStorage:', err)
    localStorage.removeItem(AUTH_TOKEN_KEY)
    localStorage.removeItem(AUTH_USER_KEY)
  }
  return null
}

const storedAuth = getStoredAuth()

const getStoredHandoffSource = (): HandoffApp | null => {
  const stored = sessionStorage.getItem(HANDOFF_SOURCE_KEY)
  return stored === 'dockit' || stored === 'sqlkit' ? stored : null
}

const getStoredRedirectAfterLogin = (): string => sessionStorage.getItem(REDIRECT_AFTER_LOGIN_KEY) ?? ''

export const useAccountStore = defineStore('account', {
  state: () =>
    <AccountStore>{
      account: {
        id: storedAuth?.id || '',
        username: storedAuth?.username || '',
        email: storedAuth?.email || '',
        password: '',
        accessToken: storedAuth?.accessToken || '',
        refreshToken: localStorage.getItem(AUTH_REFRESH_TOKEN_KEY) || '',
        fullName: storedAuth?.fullName || '',
        avatarUrl: storedAuth?.avatarUrl || '',
        language: 'en-US',
        theme: 'system',
        emailNotifications: true,
        marketingEmails: false,
      },
      handoffSource: getStoredHandoffSource(),
      showHandoffConfirm: false,
      redirectAfterLogin: getStoredRedirectAfterLogin(),
    },
  getters: {
    // accessToken: (state) => state.user.accessToken
  },
  actions: {
    setHandoffSource(source: HandoffApp | null) {
      this.handoffSource = source
      if (source) {
        sessionStorage.setItem(HANDOFF_SOURCE_KEY, source)
      } else {
        sessionStorage.removeItem(HANDOFF_SOURCE_KEY)
      }
    },
    setRedirectAfterLogin(path: string) {
      this.redirectAfterLogin = path
      if (path) {
        sessionStorage.setItem(REDIRECT_AFTER_LOGIN_KEY, path)
      } else {
        sessionStorage.removeItem(REDIRECT_AFTER_LOGIN_KEY)
      }
    },
    persistToStorage() {
      localStorage.setItem(AUTH_TOKEN_KEY, this.account.accessToken)
      if (this.account.refreshToken) {
        localStorage.setItem(AUTH_REFRESH_TOKEN_KEY, this.account.refreshToken)
      }
      localStorage.setItem(
        AUTH_USER_KEY,
        JSON.stringify({
          id: this.account.id,
          username: this.account.username,
          email: this.account.email,
          fullName: this.account.fullName,
          avatarUrl: this.account.avatarUrl,
        }),
      )
    },
    handlePostAuth(data: Record<string, unknown>) {
      markAuthHint()
      this.account.accessToken = data.access_token as string
      if (typeof data.refresh_token === 'string') {
        this.account.refreshToken = data.refresh_token
        localStorage.setItem(AUTH_REFRESH_TOKEN_KEY, data.refresh_token)
      }
      if (data.user) {
        const user = data.user as {
          id?: string
          username?: string
          email?: string
          fullName?: string
          avatarUrl?: string
        }
        this.account.id = user.id || ''
        this.account.username = user.username || ''
        this.account.email = user.email || ''
        this.account.fullName = user.fullName || ''
        this.account.avatarUrl = user.avatarUrl || ''
      }
      // Persist auth to localStorage
      this.persistToStorage()
      // target is a fullPath ("/subscribe?plan=...") — push it as a string so
      // the query survives, { path } would drop it
      const target = this.redirectAfterLogin || '/home'
      this.setRedirectAfterLogin('')
      // @ts-expect-error router is injected via pinia plugin
      this.router.push(target)
    },
    async login(
      verificationType: VerificationTypes,
      account: { email: string; password?: string; code?: string },
      turnstileToken?: string,
    ) {
      const body =
        verificationType === VerificationTypes.CODE
          ? { mode: 'code', email: account.email, code: Number(account.code), turnstile_token: turnstileToken }
          : { mode: 'password', email: account.email, password: account.password, turnstile_token: turnstileToken }

      try {
        const { data, code, messages } = (await apiClient.post(
          '/api/auth/login',
          body,
        )) as ApiClientResponse
        if (code === 2000) {
          this.handlePostAuth(data as Record<string, unknown>)
        } else {
          throw new Error(Array.isArray(messages) ? messages[0] : messages)
        }
      } catch (err) {
        throw err
      }
    },
    async loginWithGoogle(idToken: string) {
      const { data, code, messages } = (await apiClient.post('/api/auth/login', {
        mode: 'google',
        id_token: idToken,
      })) as ApiClientResponse
      if (code === 2000 && (data as Record<string, unknown>)?.needs_consent) {
        // New Google user: no access_token yet — surface it so the view opens
        // the consent flow (completeGoogleRegistration) instead of treating it
        // as a successful sign-in.
        throw { code, messages, data }
      }
      if (code === 2000) {
        this.handlePostAuth(data as Record<string, unknown>)
      } else {
        throw { code, messages, data }
      }
    },
    async completeGoogleRegistration(idToken: string) {
      const { data, code, messages } = (await apiClient.post('/api/auth/google/complete', {
        id_token: idToken,
        consent: true,
      })) as ApiClientResponse
      if (code === 2000 || code === 2001) {
        this.handlePostAuth(data as Record<string, unknown>)
      } else {
        throw { code, messages }
      }
    },
    async authRegister(params: AuthRegisterParams) {
      const { data, code, messages } = (await apiClient.post(
        '/api/auth/register',
        params,
      )) as ApiClientResponse
      if (code === 2001) {
        this.handlePostAuth(data as Record<string, unknown>)
      } else {
        throw { code, messages }
      }
    },
    async sendEmail(emailType: EmailTypes, email: string) {
      return apiClient.post('/api/accounts/mailer', { email, email_type: emailType })
    },
    async forgotPassword(email: string) {
      return apiClient.post('/api/auth/forgot-password', { email })
    },
    async validateResetToken(token: string) {
      return apiClient.post('/api/auth/validate-reset-token', {
        token,
      }) as Promise<ApiClientResponse>
    },
    async resetPassword(token: string, password: string) {
      return apiClient.post('/api/auth/reset-password', {
        token,
        password,
      }) as Promise<ApiClientResponse>
    },
    /** Called by the api client after a successful token refresh. */
    updateSession(accessToken: string, refreshToken?: string) {
      markAuthHint()
      this.account.accessToken = accessToken
      if (refreshToken) {
        this.account.refreshToken = refreshToken
        localStorage.setItem(AUTH_REFRESH_TOKEN_KEY, refreshToken)
      }
      localStorage.setItem(AUTH_TOKEN_KEY, accessToken)
    },
    clearSession() {
      clearAuthHint()
      this.account.refreshToken = ''
      localStorage.removeItem(AUTH_REFRESH_TOKEN_KEY)
    },
    logout(options?: { sessionExpired?: boolean }) {
      if (options?.sessionExpired) {
        sessionStorage.setItem(SESSION_EXPIRED_KEY, '1')
      }
      this.clearSession()
      this.account = {
        id: '',
        username: '',
        email: '',
        password: '',
        accessToken: '',
        refreshToken: '',
        fullName: '',
        avatarUrl: '',
        language: 'en-US',
        theme: 'system',
        emailNotifications: true,
        marketingEmails: false,
      }
      this.setHandoffSource(null)
      this.showHandoffConfirm = false
      this.setRedirectAfterLogin('')
      localStorage.removeItem(AUTH_TOKEN_KEY)
      localStorage.removeItem(AUTH_USER_KEY)
      // @ts-expect-error router is injected via pinia plugin
      this.router.push({ path: '/login' })
    },
    async getProfile() {
      const { data, code, messages } = (await apiClient.get(
        `/api/accounts/${this.account.id}`,
      )) as ApiClientResponse
      if (code === 2000) {
        const profile = data as Record<string, unknown>
        this.account.username = profile.username as string
        this.account.fullName = profile.fullName as string
        if (profile.avatarUrl) {
          // private bucket: swap the stored object key for a signed URL
          try {
            const avatarResp = (await apiClient.get(
              `/api/accounts/${this.account.id}/avatar`,
            )) as ApiClientResponse
            this.account.avatarUrl = ((avatarResp.data as Record<string, unknown>).avatarUrl as string) || ''
          } catch {
            this.account.avatarUrl = ''
          }
        }
        this.persistToStorage()
        return profile
      } else {
        throw { code, messages }
      }
    },
    async updateProfile(params: { username?: string; fullName?: string }) {
      const { data, code, messages } = (await apiClient.patch(
        `/api/accounts/${this.account.id}`,
        params,
      )) as ApiClientResponse
      if (code === 2000) {
        const profile = data as Record<string, unknown>
        this.account.username = profile.username as string
        this.account.fullName = profile.fullName as string
        this.persistToStorage()
        return profile
      } else {
        throw { code, messages }
      }
    },
    async uploadAvatar(file: File) {
      const formData = new FormData()
      formData.append('file', file)
      const { data, code, messages } = (await apiClient.put(
        `/api/accounts/${this.account.id}/avatar`,
        formData,
        {
          headers: { 'Content-Type': 'multipart/form-data' },
        },
      )) as ApiClientResponse
      if (code === 2000) {
        const result = data as Record<string, unknown>
        this.account.avatarUrl = result.avatarUrl as string
        this.persistToStorage()
        return result
      } else {
        throw { code, messages }
      }
    },
    async deleteAvatar() {
      const { code, messages } = (await apiClient.delete(
        `/api/accounts/${this.account.id}/avatar`,
      )) as ApiClientResponse
      if (code === 2000) {
        this.account.avatarUrl = ''
        this.persistToStorage()
      } else {
        throw { code, messages }
      }
    },
    async changePassword(params: { currentPassword: string; newPassword: string }) {
      const { code, messages } = (await apiClient.put(
        `/api/accounts/${this.account.id}/password`,
        params,
      )) as ApiClientResponse
      if (code === 2000) {
        return true
      } else {
        throw { code, messages }
      }
    },
    async deleteAccount() {
      const { code, messages } = (await apiClient.delete(
        `/api/accounts/${this.account.id}`,
      )) as ApiClientResponse
      if (code !== 2000) {
        throw { code, messages }
      }
      this.logout()
    },
    async getPreferences() {
      const { data, code, messages } = (await apiClient.get(
        `/api/accounts/${this.account.id}/preferences`,
      )) as ApiClientResponse
      if (code === 2000) {
        const prefs = data as Record<string, unknown>
        this.account.language = prefs.language as string
        this.account.theme = prefs.theme as string
        this.account.emailNotifications = prefs.emailNotifications as boolean
        this.account.marketingEmails = prefs.marketingEmails as boolean
        return prefs
      } else {
        throw { code, messages }
      }
    },
    async updatePreferences(params: {
      language?: string
      theme?: string
      emailNotifications?: boolean
      marketingEmails?: boolean
    }) {
      const { data, code, messages } = (await apiClient.patch(
        `/api/accounts/${this.account.id}/preferences`,
        params,
      )) as ApiClientResponse
      if (code === 2000) {
        const prefs = data as Record<string, unknown>
        if (prefs.language !== undefined) this.account.language = prefs.language as string
        if (prefs.theme !== undefined) this.account.theme = prefs.theme as string
        if (prefs.emailNotifications !== undefined) {
          this.account.emailNotifications = prefs.emailNotifications as boolean
        }
        if (prefs.marketingEmails !== undefined) {
          this.account.marketingEmails = prefs.marketingEmails as boolean
        }
        return prefs
      } else {
        throw { code, messages }
      }
    },
    async openInApp() {
      const app = this.handoffSource
      if (!app) return
      // the marker may sit in sessionStorage for days — hand off a fresh token
      const refreshed = await refreshSession()
      if (!refreshed) {
        // dead lease: a dead-token handoff only bounces the user back to
        // "Session expired" in the desktop app — re-login is the honest path
        this.logout({ sessionExpired: true })
        return
      }
      const params = new URLSearchParams({
        token: this.account.accessToken,
        username: this.account.username,
        email: this.account.email,
        userId: this.account.id,
        avatar: this.account.avatarUrl || '',
      })
      window.location.href = `${app}://auth?${params.toString()}`
      this.showHandoffConfirm = false
      this.setHandoffSource(null)
    },
    continueToWeb() {
      this.showHandoffConfirm = false
      this.setHandoffSource(null)
    },
  },
})
